import { useState } from 'react'
import { ChevronDown, Plus, Trash2 } from 'lucide-react'
import { Input, Switch } from '@heroui/react'
import { Button as HeroButton } from '@heroui/react'
import { Button } from '@trafiflow/shared-ui'
import { TriggerEditor } from './TriggerEditor'
import { ActionEditor } from './ActionEditor'
import type { FunnelRule, RuleLogic, RuleTrigger, RuleAction } from '../../types'

interface RuleCardProps {
  rule: FunnelRule
  onUpdate: (updates: Partial<FunnelRule>) => void
  onDelete: () => void
  onAddTrigger: () => void
  onUpdateTrigger: (triggerId: string, updates: Partial<RuleTrigger>) => void
  onDeleteTrigger: (triggerId: string) => void
  onAddAction: () => void
  onUpdateAction: (actionId: string, updates: Partial<RuleAction>) => void
  onDeleteAction: (actionId: string) => void
}

export function RuleCard({
  rule,
  onUpdate,
  onDelete,
  onAddTrigger,
  onUpdateTrigger,
  onDeleteTrigger,
  onAddAction,
  onUpdateAction,
  onDeleteAction,
}: RuleCardProps) {
  const [isExpanded, setIsExpanded] = useState(true)

  return (
    <div
      className="rounded-lg border overflow-hidden border-fn-border bg-fn-card"
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-fn-border">
        <button
          type="button"
          className="p-0.5"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <ChevronDown
            className="w-4 h-4 transition-transform text-fn-text-muted"
            style={{
              transform: isExpanded ? 'rotate(0)' : 'rotate(-90deg)',
            }}
          />
        </button>

        <Input
          size="sm"
          variant="underlined"
          value={rule.name}
          onValueChange={(val) => onUpdate({ name: val })}
          classNames={{ input: 'text-sm font-medium', inputWrapper: 'border-none shadow-none' }}
          aria-label="Rule name"
        />

        <div className="flex items-center gap-2 ml-auto flex-shrink-0">
          <button
            type="button"
            className={`px-2 py-0.5 rounded text-xs font-bold border ${rule.logic === 'AND' ? 'text-fn-primary' : 'text-fn-text-sub'}`}
            style={{
              borderColor: 'var(--fn-border)',
            }}
            onClick={() => onUpdate({ logic: (rule.logic === 'AND' ? 'OR' : 'AND') as RuleLogic })}
          >
            {rule.logic}
          </button>
          <Switch
            size="sm"
            isSelected={rule.isActive}
            onValueChange={(val) => onUpdate({ isActive: val })}
            aria-label="Rule active"
          />
          <HeroButton isIconOnly size="sm" variant="light" color="danger" onPress={onDelete}>
            <Trash2 className="w-3.5 h-3.5" />
          </HeroButton>
        </div>
      </div>

      {/* Body */}
      {isExpanded && (
        <div className="p-3 space-y-3 animate-slide-down">
          {/* Triggers */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold uppercase text-fn-text-muted">
                When ({rule.triggers.length})
              </span>
              <Button variant="secondary" className="text-xs h-6 px-2" onClick={onAddTrigger}>
                <Plus className="w-3 h-3 mr-1" /> Trigger
              </Button>
            </div>
            <div className="space-y-1.5">
              {rule.triggers.map((trigger) => (
                <TriggerEditor
                  key={trigger.id}
                  trigger={trigger}
                  onChange={(updates) => onUpdateTrigger(trigger.id, updates)}
                  onDelete={() => onDeleteTrigger(trigger.id)}
                />
              ))}
              {rule.triggers.length === 0 && (
                <p className="text-xs py-2 text-center text-fn-text-muted">
                  No triggers. Add one to define when this rule fires.
                </p>
              )}
            </div>
          </div>

          {/* Actions */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold uppercase text-fn-text-muted">
                Then ({rule.actions.length})
              </span>
              <Button variant="secondary" className="text-xs h-6 px-2" onClick={onAddAction}>
                <Plus className="w-3 h-3 mr-1" /> Action
              </Button>
            </div>
            <div className="space-y-1.5">
              {rule.actions.map((action) => (
                <ActionEditor
                  key={action.id}
                  action={action}
                  onChange={(updates) => onUpdateAction(action.id, updates)}
                  onDelete={() => onDeleteAction(action.id)}
                />
              ))}
              {rule.actions.length === 0 && (
                <p className="text-xs py-2 text-center text-fn-text-muted">
                  No actions. Add one to define what happens.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
